import { Utils } from '@wix/common-pro-gallery-worker-wrapper';

import { Consts, GALLERY_v4_CONSTS } from '@wix/photography-client-lib';


export default class Fetcher {
  constructor({
    context,
    controllerConfig,
    reportBiLog,
    SSRWorkerLog,
    withWarmupData,
  }) {
    this.SSRWorkerLog = SSRWorkerLog;

    this.ITEMS_BATCH_SIZE = 25;
    this.controllerConfig = controllerConfig;
    this.wixCodeApi = controllerConfig.wixCodeApi;
    this.viewMode = Utils.parseViewMode(this.wixCodeApi.window.viewMode);
    this.context = context.getContext();
    this.baseUrl = Utils.getBaseUrl(this.wixCodeApi, this.viewMode);
    this.compId = controllerConfig.compId;
    this.externalId =
      controllerConfig.config.externalId ||
      '00000000-0000-0000-0000-000000000000'; // externalId of a new site before publish in Preview is "", so we protect here undefined and ""
    this.instance = controllerConfig.appParams.instance;
    this.instanceId = controllerConfig.appParams.instanceId;
    this.reportBiLog = reportBiLog;
    this.withWarmupData = withWarmupData;
    this.initDataSavedState();
  }

  isSSR() {
    return this.wixCodeApi.window.rendering.env === 'backend';
  }

  async fetchGalleryId() {
    if (this.galleryId) {
      return;
    }
    await this.getGallerySettings();
    if (!this.galleryId) {
      console.error('Could not get galleryId for artStore');
    }
    return;
  }

  addSsrUrlParams(url) {
    return (url += this.isSSR()
      ? '&petri_ovr=specs.SkipCachePopulationSpec:true'
      : '');
  }

  async getGalleryDataFromServer({ from, batchSize = this.ITEMS_BATCH_SIZE }) {
    await Utils.verifyExperiments(this.scopedGlobalSdkApis); // make sure experiments were requestd and are ready before proceeding

    const galleryData = await this.getDataFromServer({
      from,
      batchSize,
    });
    const { pageResponse, galleryId, dateCreated } = galleryData;
    const { items, totalItemsCount, settings } = pageResponse;
    const data = { items, totalItemsCount, galleryId, settings, dateCreated };

    return data;
  }

  async getDataFromServer({ from, batchSize }) {
    const to = from + batchSize;
    const requestUrl = this.addSsrUrlParams(
      `${this.baseUrl}/pro-gallery-webapp/api/v1/gallery/${this.instanceId}/items/from/${from}/to/${to}?compId=${this.compId}&externalId=${this.externalId}&cache_buster=${Math.random()}`,
    );
    this.SSRWorkerLog.push('trying to get items from the server ', requestUrl);
    return this.fetchNonPlatformizedServerData({
      requestUrl,
      multilingualHeader: this.getMultiLangHeader(),
    });
  }

  async fetchNonPlatformizedServerData({ requestUrl, multilingualHeader }) {
    const response = await fetch(requestUrl, {
      headers: {
        'x-wix-linguist': multilingualHeader,
      },
    });
    const data = await response.json();
    return data;
  }

  initDataSavedState() {
    if (
      this.viewMode === GALLERY_v4_CONSTS.viewMode.PREVIEW ||
      this.viewMode === GALLERY_v4_CONSTS.viewMode.EDIT
    ) {
      // encode instance only when needed (in preview or editor only), too have for live site.
      const encodedInstance = this.instance.substring(
        this.instance.indexOf('.') + 1,
      );
      const isDemoMode = JSON.parse(atob(encodedInstance)).demoMode;
      this.dataSavedState = isDemoMode
        ? Consts.dataSavedState.PUBLISHED
        : Consts.dataSavedState.SAVED;
    } else {
      this.dataSavedState = Consts.dataSavedState.PUBLISHED;
    }
  }

  getMultiLangFields() {
    const currentShortLang = this.wixCodeApi.window.multilingual
      .currentLanguage;
    const currentLang = this.wixCodeApi.window.multilingual.siteLanguages.filter(
      (lang) => lang.languageCode === currentShortLang,
    );
    if (currentLang.length > 0) {
      return {
        isPrimaryLanguage: currentLang[0].isPrimaryLanguage,
        lang: currentShortLang,
        locale: currentLang[0].locale,
      };
    }

    return null;
  }

  getMultiLangHeader() {
    const fields = this.getMultiLangFields();
    const header = fields
      ? `${fields.lang}|${
          fields.locale
        }|${fields.isPrimaryLanguage.toString()}|${this.instanceId}`
      : '';
    this.SSRWorkerLog.push('Multilingual header: ' + header);
    return header;
  }

  getLangQuery() {
    const fields = this.getMultiLangFields();
    const query = fields ? `${fields.lang}${fields.locale}` : '';
    return query;
  }

  async getGallerySettings() {
    try {
      this.setGalleryData(await this.getGalleryDataFromServer({ from: 0 }));
    } catch (error) {
      console.error('Failed to get gallery settings: ' + error);
    }
  }

  setGalleryData({ galleryId, settings, dateCreated }) {
    if (galleryId) {
      this.galleryId = galleryId;
      this.dateCreated = dateCreated;
      this.setSettings(settings);
      return { galleryId, dateCreated, settings: this.settings };
    }
  }

  requireManualGalleryDataSet = true;

  setSettings(settings) {
    const parseSettings = (_settings) => {
      if (typeof _settings === 'string') {
        try {
          return JSON.parse(_settings);
        } catch (e) {
          console.error('Failed parse settting', e);
          return _settings;
        }
      }
      return _settings;
    };
    const prevSettings = parseSettings(this.settings);
    const nextSettings = parseSettings(settings);
    this.settings = { ...prevSettings, ...nextSettings };
  }

  async getItemByIdFromServer(itemId) {
    const requestUrl = `${this.baseUrl}/pro-gallery-webapp/api/v1/gallery/components/${this.compId}/items/${itemId}?instanceId=${this.instanceId}&externalId=${this.externalId}&cache_buster=${Math.random()}`;
    return this.fetchNonPlatformizedServerData({
      requestUrl,
      multilingualHeader: this.getMultiLangHeader(),
    });
  }
}
